import {
    getUserAnalytics,
    refreshUserAnalytics,
    getRefreshStatus,
    exportUserAnalytics,
} from '@/api/userAnalytics.js';
import constants from '@/constants';

const { FILTER_ALL, REFRESH_STATUS } = constants;

export default {
    state: {
        analytics: [],
        isLoading: false,

        // Pagination (page-based)
        pagination: {
            total: 0,
            page: 1,
            perPage: 50,
            totalPages: 0,
        },

        // Filters
        filters: {
            userId: null,
            email: '',
            engagementTier: FILTER_ALL,
            recencyStatus: FILTER_ALL,
        },

        // Sorting
        sortBy: 'user_id',
        sortOrder: 'desc',

        // Refresh status
        refreshStatus: {
            lastRefreshAt: null,
            refreshDurationSeconds: null,
            status: REFRESH_STATUS.IDLE,
            errorMessage: null,
            updatedAt: null,
        },
        isRefreshing: false,
        isExporting: false,
    },

    getters: {
        USER_ANALYTICS: (state) => state.analytics,
        USER_ANALYTICS_LOADING: (state) => state.isLoading,
        USER_ANALYTICS_PAGINATION: (state) => state.pagination,
        USER_ANALYTICS_FILTERS: (state) => state.filters,
        USER_ANALYTICS_SORT_BY: (state) => state.sortBy,
        USER_ANALYTICS_SORT_ORDER: (state) => state.sortOrder,
        USER_ANALYTICS_CURRENT_PAGE: (state) => state.pagination.page,
        USER_ANALYTICS_TOTAL_PAGES: (state) => state.pagination.totalPages,
        USER_ANALYTICS_REFRESH_STATUS: (state) => state.refreshStatus,
        USER_ANALYTICS_IS_REFRESHING: (state) => state.isRefreshing,
        USER_ANALYTICS_IS_EXPORTING: (state) => state.isExporting,
    },

    mutations: {
        SET_USER_ANALYTICS(state, data) {
            state.analytics = data;
        },
        SET_USER_ANALYTICS_LOADING(state, value) {
            state.isLoading = value;
        },
        SET_USER_ANALYTICS_PAGINATION(state, pagination) {
            state.pagination = {
                total: pagination.total,
                page: pagination.page,
                perPage: pagination.per_page,
                totalPages: pagination.total_pages,
            };
        },
        SET_USER_ANALYTICS_PAGE(state, page) {
            state.pagination.page = page;
        },
        SET_USER_ANALYTICS_PER_PAGE(state, perPage) {
            state.pagination.perPage = perPage;
            state.pagination.page = 1;
        },
        SET_USER_ANALYTICS_FILTERS(state, filters) {
            state.filters = filters;
        },
        SET_USER_ANALYTICS_SORT(state, { sortBy, sortOrder }) {
            state.sortBy = sortBy;
            state.sortOrder = sortOrder;
        },
        RESET_USER_ANALYTICS_FILTERS(state) {
            state.filters = {
                userId: null,
                email: '',
                engagementTier: FILTER_ALL,
                recencyStatus: FILTER_ALL,
            };
            state.pagination.page = 1;
        },
        SET_USER_ANALYTICS_REFRESH_STATUS(state, status) {
            state.refreshStatus = {
                lastRefreshAt: status.last_refresh_at,
                refreshDurationSeconds: status.refresh_duration_seconds,
                status: status.status,
                errorMessage: status.error_message,
                updatedAt: status.updated_at,
            };
        },
        SET_USER_ANALYTICS_IS_REFRESHING(state, value) {
            state.isRefreshing = value;
        },
        SET_USER_ANALYTICS_IS_EXPORTING(state, value) {
            state.isExporting = value;
        },
    },

    actions: {
        async loadUserAnalytics({ commit, state }) {
            commit('SET_USER_ANALYTICS_LOADING', true);

            try {
                const params = {
                    page: state.pagination.page,
                    per_page: state.pagination.perPage,
                    sort_by: state.sortBy,
                    sort_order: state.sortOrder,
                };

                // Add filters only if not default
                if (state.filters.userId) {
                    params.user_id = state.filters.userId;
                }
                if (state.filters.email) {
                    params.email = state.filters.email;
                }
                if (state.filters.engagementTier !== FILTER_ALL) {
                    params.engagement_tier = state.filters.engagementTier;
                }
                if (state.filters.recencyStatus !== FILTER_ALL) {
                    params.recency_status = state.filters.recencyStatus;
                }

                const response = await getUserAnalytics(params);

                if (response) {
                    commit('SET_USER_ANALYTICS', response.data);
                    commit('SET_USER_ANALYTICS_PAGINATION', response);
                }
            } catch (error) {
                console.error('Error loading user analytics:', error);
                commit('SET_USER_ANALYTICS', []);
            } finally {
                commit('SET_USER_ANALYTICS_LOADING', false);
            }
        },

        async changeAnalyticsPage({ commit, dispatch }, page) {
            commit('SET_USER_ANALYTICS_PAGE', page);
            await dispatch('loadUserAnalytics');
        },

        async triggerRefresh({ commit }) {
            commit('SET_USER_ANALYTICS_IS_REFRESHING', true);

            try {
                const response = await refreshUserAnalytics();
                return response;
            } catch (error) {
                console.error('Error triggering refresh:', error);
                commit('SET_USER_ANALYTICS_IS_REFRESHING', false);
                return null;
            }
        },

        async loadRefreshStatus({ commit }) {
            try {
                const response = await getRefreshStatus();

                if (response) {
                    commit('SET_USER_ANALYTICS_REFRESH_STATUS', response);
                    commit(
                        'SET_USER_ANALYTICS_IS_REFRESHING',
                        response.status === REFRESH_STATUS.REFRESHING
                    );
                }

                return response;
            } catch (error) {
                console.error('Error loading refresh status:', error);
                return null;
            }
        },

        async exportUserAnalytics({ commit, state }) {
            commit('SET_USER_ANALYTICS_IS_EXPORTING', true);

            try {
                const params = {
                    sort_by: state.sortBy,
                    sort_order: state.sortOrder,
                };

                if (state.filters.userId) {
                    params.user_id = state.filters.userId;
                }
                if (state.filters.email) {
                    params.email = state.filters.email;
                }
                if (state.filters.engagementTier !== FILTER_ALL) {
                    params.engagement_tier = state.filters.engagementTier;
                }
                if (state.filters.recencyStatus !== FILTER_ALL) {
                    params.recency_status = state.filters.recencyStatus;
                }

                const response = await exportUserAnalytics(params);

                if (response && response.data) {
                    const disposition =
                        response.headers?.['content-disposition'] || '';
                    const match = disposition.match(/filename="?([^";]+)"?/i);
                    const fileName =
                        (match && match[1]) || 'user_analytics_export.csv';

                    const blob = new Blob([response.data], {
                        type: 'text/csv;charset=utf-8;',
                    });
                    const link = document.createElement('a');
                    const url = window.URL.createObjectURL(blob);
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                }

                return response;
            } catch (error) {
                console.error('Error exporting user analytics:', error);
                return null;
            } finally {
                commit('SET_USER_ANALYTICS_IS_EXPORTING', false);
            }
        },
    },
};
